import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useSessionContext } from "./SessionContext";

/**
 * EditPageContext — the page that canvas edits target.
 *
 * Domain Managers (Token/Drawing/Fog/Wall/Note/Pin/DragDrop/MapObjects/Text/
 * LocationLink) send their WS edits to a page id. On the table that page is the
 * ACTIVE page (`outgoingPageId`, the one broadcast to players) — which is also
 * the page the GM is looking at, so the two coincide.
 *
 * GM Studio breaks that coincidence: it previews a page (`viewingPageId`) that
 * may NOT be the active one, so edits made in the studio must target the
 * PREVIEWED page — never the active page by default (when the two are the
 * same page, the players see the edits as usual). This context lets the studio
 * override the edit-target for the canvas subtree it mounts.
 *
 * `useEditPageId()` returns the override when a provider is present (studio) and
 * falls back to `outgoingPageId` otherwise (table) — so the table is byte-for-
 * byte unchanged (no provider ⇒ identical value), while the studio subtree
 * retargets every Manager at once by re-sourcing this single value.
 *
 * `onOpenPage` is how the studio opens another page from its canvas (a location
 * link): in the studio workspace, never by moving the players.
 */
interface EditPageValue {
  readonly pageId: string;
  readonly onOpenPage?: (pageId: string) => void;
}

const EditPageContext = createContext<EditPageValue | null>(null);

export function EditPageProvider({
  pageId,
  onOpenPage,
  children,
}: {
  pageId: string;
  onOpenPage?: (pageId: string) => void;
  children: ReactNode;
}) {
  // Stable value: every canvas Manager consumes it.
  const value = useMemo(() => ({ pageId, onOpenPage }), [pageId, onOpenPage]);
  return (
    <EditPageContext.Provider value={value}>
      {children}
    </EditPageContext.Provider>
  );
}

/** The page canvas edits should target: studio override, else the active page. */
export function useEditPageId(): string {
  const override = useContext(EditPageContext);
  const { outgoingPageId } = useSessionContext();
  return override?.pageId ?? outgoingPageId;
}

/** Studio: open a page in the workspace. undefined at the table (no provider). */
export function useOpenPageInPlace(): ((pageId: string) => void) | undefined {
  return useContext(EditPageContext)?.onOpenPage;
}
